// The RBP Shopping List (`/rbp`): the programme header in place of the depot shopping navigation
// (RBP-D39), the totals and details strip (RBP-D18) and the benefit group rows (RBP-D38). The page
// spans the five RBP depots, so it needs no ordering choice: SL1.0 opens it from the depot chooser
// (`choose.htm:23`) before any depot is chosen.
//
// States: loading; a failure with a retry; "no list for this store" when the store does not take
// part (the 404); and the list, whose frame is drawn with blank values when no report is current
// yet (the 204).
//
// Each group row carries SL1.0's ellipsis menu with "Clear selection" (the group's selections; SL1.0's
// "Deselect", renamed with the check marks gone, ADO-4037), and the totals strip one for every
// selection; both clear without a confirmation, as SL1.0 does, then
// re-read the header and the rows and close the open group. A stored quantity re-reads them too.
//
// Each menu's second item, "Add to cart", and the header's "Add selected to cart" (every
// selection) start SL1.0's add (`rbp_report.htm:133-141`, `rbp_report_header.htm:66-72`,
// `nav.php:307-324`): the confirmation per depot, then the add. Nothing to add is a banner under
// the totals, with no dialog. After an add the list is read again and the open group closes, as
// SL1.0 reloads the page. The add never changes the active depot.
import { useCallback, useRef, useState } from 'react';
import { deselectRbpAll, deselectRbpGroup, type RbpBenefitGroupRow } from '../../api/rbp.client';
import { Banner, Button } from '../../design-system';
import { createRbpPendingSelections } from '../../rbp/rbpPendingSelections';
import { useRbpCartAddition } from '../../rbp/useRbpCartAddition';
import { useRbpShoppingList } from '../../rbp/useRbpShoppingList';
import { BenefitGroupMenu } from './BenefitGroupMenu';
import { BenefitGroupTable } from './BenefitGroupTable';
import { RbpCartAdditionDialog } from './RbpCartAdditionDialog';
import { RbpProgrammeHeader } from './RbpProgrammeHeader';
import { RbpTotalsStrip } from './RbpTotalsStrip';
import styles from './RbpShoppingListPage.module.css';

export function RbpShoppingListPage() {
  const list = useRbpShoppingList();
  const header = list.status === 'ready' ? list.header : null;
  const { refresh } = list;
  const [collapseSignal, setCollapseSignal] = useState(0);
  const [deselectFailed, setDeselectFailed] = useState(false);
  // Shared across the open group's line selections, so a Deselect can let their in-flight writes
  // settle before its DELETE: no PUT can then reach the server after the DELETE and re-create a row.
  const pending = useRef(createRbpPendingSelections()).current;

  // Declared before the deselect so a change to the selections can clear a stale notice.
  const afterAdd = useCallback(() => {
    refresh();
    setCollapseSignal((count) => count + 1);
  }, [refresh]);
  const addition = useRbpCartAddition(afterAdd);
  const { clearNotice } = addition;
  const selectionSaved = useCallback(() => {
    clearNotice();
    refresh();
  }, [clearNotice, refresh]);

  const deselect = useCallback(
    async (clear: () => Promise<void>) => {
      clearNotice();
      setDeselectFailed(false);
      // Latch synchronously, before any await: a step in the same tick as the Deselect, or while it
      // waits for the in-flight writes and the DELETE, must not start a PUT that lands after it.
      pending.beginClear();
      try {
        await pending.settled();
        await clear();
      } catch {
        setDeselectFailed(true);
      } finally {
        pending.endClear();
      }
      refresh();
      setCollapseSignal((count) => count + 1);
    },
    [clearNotice, pending, refresh],
  );
  const addBusy = addition.state.status === 'previewing' || addition.state.status === 'adding';
  const { start: startAddition } = addition;
  const addAll = useCallback(() => startAddition({ benefitGroupCode: null }), [startAddition]);
  const groupMenu = (row: RbpBenefitGroupRow) => (
    <BenefitGroupMenu
      label={`Actions for ${row.description}`}
      items={[
        {
          key: 'deselect',
          label: 'Clear selection',
          onSelect: () => void deselect(() => deselectRbpGroup(row.benefitGroupCode)),
        },
        {
          key: 'add-to-cart',
          label: 'Add to cart',
          onSelect: () => startAddition({ benefitGroupCode: row.benefitGroupCode }),
        },
      ]}
    />
  );

  return (
    <div className={styles.page}>
      <RbpProgrammeHeader
        quarterClosesOn={header?.quarterClosesOn ?? null}
        onAddSelected={list.status === 'ready' ? addAll : undefined}
        addBusy={addBusy}
      />
      <main className={styles.main}>
        <h1 className={styles.heading}>RBP Shopping List</h1>
        {list.status === 'loading' && (
          <p role="status" className={styles.status}>
            Loading your RBP Shopping List…
          </p>
        )}
        {list.status === 'error' && (
          <div className={styles.notice}>
            <Banner type="error">
              We couldn&rsquo;t load your RBP Shopping List. Please try again.
            </Banner>
            <Button variant="secondary" onClick={list.retry}>
              Try again
            </Button>
          </div>
        )}
        {list.status === 'not-a-participant' && (
          <Banner type="info">
            Your store does not take part in the Retailer Benefit Programme.
          </Banner>
        )}
        {list.status === 'ready' && (
          <>
            {deselectFailed ? (
              <Banner type="error">
                We couldn&rsquo;t clear your selection. Please try again.
              </Banner>
            ) : null}
            <RbpTotalsStrip
              header={list.header}
              onDeselectAll={() => void deselect(deselectRbpAll)}
              onAddAll={addAll}
            />
            {addition.state.status === 'nothing-to-add' ? (
              <Banner type="info">No products to add!</Banner>
            ) : null}
            {addition.state.status === 'failed' ? (
              <Banner type="error">{addition.state.message}</Banner>
            ) : null}
            <BenefitGroupTable
              rows={list.groups}
              renderRowExtra={groupMenu}
              onSelectionSaved={selectionSaved}
              collapseSignal={collapseSignal}
              pending={pending}
            />
          </>
        )}
      </main>
      <RbpCartAdditionDialog
        state={addition.state}
        onConfirm={addition.confirm}
        onClose={addition.dismiss}
      />
    </div>
  );
}
